import { useContext, useRef, useState } from "react";
import { OverlayPanel } from "primereact/overlaypanel";
import { ProgressSpinner } from "primereact/progressspinner";
import styles from "./GoogleGroupIcon.module.css";
import { ThemeContext } from "../../themes/Theme";
import { GOOGLE_GROUP_NAMES } from "./group-names";
import { useGoogleGroupStats } from "../../pages/specialty-assistant/data";
import { formatAbbreviatedMoney } from "../../pages/specialty-assistant/Money";

const GROUP_COLORS_LIGHT = [
  { bg: "#dbeafe", text: "#1e40af" }, // blue
  { bg: "#dcfce7", text: "#166534" }, // green
  { bg: "#fef3c7", text: "#92400e" }, // amber
  { bg: "#fce7f3", text: "#9d174d" }, // pink
  { bg: "#e0e7ff", text: "#3730a3" }, // indigo
  { bg: "#ffedd5", text: "#9a3412" }, // orange
  { bg: "#f3e8ff", text: "#6b21a8" }, // purple
  { bg: "#ccfbf1", text: "#115e59" }, // teal
  { bg: "#fee2e2", text: "#991b1b" }, // red
  { bg: "#ecfccb", text: "#3f6212" }, // lime
  { bg: "#e0f2fe", text: "#075985" }, // sky
  { bg: "#fae8ff", text: "#86198f" }, // fuchsia
] as const;

const GROUP_COLORS_DARK = [
  { bg: "#1e3a5f", text: "#93c5fd" }, // blue
  { bg: "#14432a", text: "#86efac" }, // green
  { bg: "#4a3012", text: "#fcd34d" }, // amber
  { bg: "#4a1942", text: "#f9a8d4" }, // pink
  { bg: "#2e2668", text: "#a5b4fc" }, // indigo
  { bg: "#4a2512", text: "#fdba74" }, // orange
  { bg: "#3b1768", text: "#d8b4fe" }, // purple
  { bg: "#134040", text: "#5eead4" }, // teal
  { bg: "#4a1818", text: "#fca5a5" }, // red
  { bg: "#2a3f12", text: "#bef264" }, // lime
  { bg: "#0c3a5a", text: "#7dd3fc" }, // sky
  { bg: "#4a1260", text: "#f0abfc" }, // fuchsia
] as const;

function hashString(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
    hash = hash & hash;
  }
  return Math.abs(hash);
}

function extractTeamName(groupName: string): string {
  let inner = groupName;
  if (inner.startsWith("team")) {
    inner = inner.slice(4);
  }
  if (inner.endsWith("submissions")) {
    inner = inner.slice(0, -11);
  }
  return inner;
}

function formatPercent(value: number): string {
  return `${(value * 100).toFixed(1)}%`;
}

export const GoogleGroupIcon = ({
  groupName,
}: {
  groupName: string | null;
}) => {
  const { theme } = useContext(ThemeContext);
  const isLightMode = theme === "light";
  const op = useRef<OverlayPanel | null>(null);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const stats = useGoogleGroupStats(popoverOpen ? groupName : null);

  if (!groupName) {
    return <></>;
  }

  const groupNameWithoutEmail = groupName.split("@")[0];
  const colors = isLightMode ? GROUP_COLORS_LIGHT : GROUP_COLORS_DARK;
  const colorIndex = hashString(groupNameWithoutEmail) % colors.length;
  const color = colors[colorIndex];
  const teamName = extractTeamName(groupNameWithoutEmail);
  const displayInitials = teamName.slice(0, 2).toUpperCase();
  const displayName = GOOGLE_GROUP_NAMES[groupName] ?? "";

  return (
    <div className={styles.container}>
      <div
        className={styles.badge}
        style={{
          backgroundColor: color.bg,
          color: color.text,
          border: `1px solid color-mix(in srgb, ${color.text} 25%, transparent)`,
          cursor: "pointer",
        }}
        onClick={(e) => {
          e.stopPropagation();
          setPopoverOpen(true);
          op.current?.toggle(e);
        }}
      >
        <span className={styles.label}>{displayInitials}</span>
      </div>
      <span className={styles.teamNameFull}>{displayName}</span>
      <OverlayPanel
        ref={op}
        onClick={(e) => e.stopPropagation()}
        dismissable={true}
        onHide={() => setPopoverOpen(false)}
      >
        <div className={styles.popoverContent}>
          <div className={styles.popoverHeader}>
            <div
              className={styles.popoverBadge}
              style={{
                backgroundColor: color.bg,
                color: color.text,
                border: `1px solid color-mix(in srgb, ${color.text} 25%, transparent)`,
              }}
            >
              <span>{displayInitials}</span>
            </div>
            <div className={styles.popoverTitle}>
              {displayName || groupNameWithoutEmail}
            </div>
          </div>
          <div className={styles.popoverEmail}>{groupName}</div>
          {stats.isLoading ? (
            <div className={styles.popoverLoading}>
              <ProgressSpinner
                style={{ width: "28px", height: "28px" }}
                strokeWidth="3"
              />
            </div>
          ) : stats.data ? (
            <div className={styles.popoverStats}>
              <div className={styles.statRow}>
                <span className={styles.statLabel}>Submissions</span>
                <span className={styles.statValue}>
                  {stats.data.total_submissions}
                </span>
              </div>
              <div className={styles.statRow}>
                <span className={styles.statLabel}>Quote Rate</span>
                <span className={styles.statValue}>
                  {formatPercent(stats.data.quote_rate)}
                </span>
              </div>
              <div className={styles.statRow}>
                <span className={styles.statLabel}>Bind Rate</span>
                <span className={styles.statValue}>
                  {formatPercent(stats.data.bind_rate)}
                </span>
              </div>
              <div className={styles.statRow}>
                <span className={styles.statLabel}>Avg TIV</span>
                <span className={styles.statValue}>
                  {formatAbbreviatedMoney(stats.data.avg_tiv)}
                </span>
              </div>
            </div>
          ) : null}
        </div>
      </OverlayPanel>
    </div>
  );
};
